/* eslint-disable arrow-body-style */

import {providers} from "./users/usersReducers";

const DEBUG_ACTIONS = false;
/**
 * makes a one-field object from a name/value pair
 *
 * @param name {string}
 * @param value {any}
 */
function obj(name, value) {
  const out = {};
  out[name] = value;
  return out;
}

/**
 * makes an action; no assumption is made about its inner workings.
 *
 * @param mutators {Map}
 * @param type {string} (symbol?)
 * @param mutator {function}
 * @returns {function(*): {type: *, payload: *}}
 */
function makeAction(mutators, type, mutator) {
  try {
    if (!(mutators instanceof Map)) {
      throw new Error('bad makeAction map: ');
    }
    if (!(typeof mutator === 'function')) {
      throw new Error('bad makeAction mutator: ');
    }
    if (!(typeof type === 'string')) {
      throw new Error(`bad makeAction type: ${type}`);
    }
  } catch (err) {
    console.log('bad makeAction:', mutators, type, mutator);
    throw err;
  }
  mutators.set(type, mutator);
  return (payload) => {
    if (DEBUG_ACTIONS) console.log('--- action:', type, payload);
    return ({ type, payload });
  };
}

/**
 * takes the parameter and updates state by setting that value
 * to the key defined in name
 *
 * @param mutators
 * @param type
 * @param name
 * @returns {function(*): {type: *, payload: *}}
 */

function keyToValueAction(mutators, type, name) {
  return makeAction(mutators, type, (state, value) => {
    const out = { ...state };
    out[name] = value;
    if (DEBUG_ACTIONS) console.log(type, '>>> setting ', name, 'to', value, '->>>', out);
    return out;
  });
}

/**
 * makes an action that updates some of the state.
 * If mutatorOrKey is a string, this returns a one-argument action
 * that sets a field to a value within state.
 *
 * If mutatorOrKey is a function it updates state with the result of the function.
 * This is analogous to how "setState" in components work- it revises/adds the result of the
 * function to the state.
 *
 * @param mutators {Map}
 * @param type {string}
 * @param mutatorOrKey {string | function}
 * @returns {function(*): {type: *, payload: *}}
 */
function updateAction(mutators, type, mutatorOrKey) {
  if (typeof mutatorOrKey === 'string') {
    return keyToValueAction(mutators, type, mutatorOrKey);
  }
  return makeAction(mutators, type, (state, ...args) => {
    return Object.assign({}, state, mutatorOrKey(state, ...args));
  });
}


/**
 * makes an action that sets a field to a value.
 * This action does not have any interest/variance from the payload.
 *
 * @param mutators {Map}
 * @param type {string}
 * @param name {string}
 * @param value any
 * @returns {function(*): {type: *, payload: *}}
 */
function makeSetValueAction(mutators, type, name, value) {
  return updateAction(mutators, type, () => obj(name, value));
}

function makeDispatchFromProviders(prov) {
  return dispatch => Object.keys(prov)
    .reduce((dispatcher, name) => ({
      ...dispatcher,
      [name]: (...args) => dispatch(prov[name](...args)),
    }), {});
}

export {
  makeDispatchFromProviders,
  makeAction,
  makeSetValueAction,
  updateAction,
  obj,
  keyToValueAction,
};
